import type { SVGProps } from 'react'

/**
 * Corsalia icon set — hand-drawn gold line-work that replaces the emoji used
 * across the game UI. Every icon inherits `currentColor` so you size/colour it
 * with Tailwind (`className="w-5 h-5 text-sand-300"`). 24×24 unless noted.
 */
type IconProps = SVGProps<SVGSVGElement>

function Svg({ children, ...p }: IconProps & { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      {children}
    </svg>
  )
}

/* ---------- brand ---------- */
export const AnchorIcon = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="5" r="2" /><path d="M12 7v13" /><path d="M8 11H6m12 0h-2" /><path d="M5 13a7 7 0 0 0 14 0" /></Svg>
)

// Discord wordmark glyph — fill-based (not stroke), so it doesn't use <Svg>.
export const DiscordIcon = (p: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
  </svg>
)

/* ---------- resources ---------- */
export const GoldIcon = (p: IconProps) => (
  <Svg {...p}><ellipse cx="12" cy="6.5" rx="6" ry="2.6" /><path d="M6 6.5v4c0 1.45 2.7 2.6 6 2.6s6-1.15 6-2.6v-4" /><path d="M6 10.5v4c0 1.45 2.7 2.6 6 2.6s6-1.15 6-2.6v-4" /></Svg>
)
export const WoodIcon = (p: IconProps) => (
  <Svg {...p}><path d="M7 13.2 L10 11.6M7 18.8 L10 17.2" /><path d="M10 11.6 a2.8 2.8 0 0 1 0 5.6" /><ellipse cx="7" cy="16" rx="2.2" ry="2.8" /><ellipse cx="7" cy="16" rx="0.8" ry="1.1" /><path d="M13 13.2 L16 11.6M13 18.8 L16 17.2" /><path d="M16 11.6 a2.8 2.8 0 0 1 0 5.6" /><ellipse cx="13" cy="16" rx="2.2" ry="2.8" /><ellipse cx="13" cy="16" rx="0.8" ry="1.1" /><path d="M10 7.2 L13 5.6M10 12.8 L13 11.2" /><path d="M13 5.6 a2.8 2.8 0 0 1 0 5.6" /><ellipse cx="10" cy="10" rx="2.2" ry="2.8" /><ellipse cx="10" cy="10" rx="0.8" ry="1.1" /></Svg>
)
export const RumIcon = (p: IconProps) => (
  <Svg {...p}><path d="M8 4 Q5.4 12 8 20 L16 20 Q18.6 12 16 4 Z" /><path d="M6.6 9h10.8M6.6 15h10.8" /></Svg>
)
export const PrestigeIcon = (p: IconProps) => (
  <Svg {...p}><path d="M12 3l2.3 6.2L21 9.4l-5 4 1.7 6.6L12 16.4 6.3 20l1.7-6.6-5-4 6.7-.2z" /></Svg>
)

export type ResourceName = 'gold' | 'wood' | 'rum' | 'prestige'
export function ResourceIcon({ name, ...p }: IconProps & { name: ResourceName }) {
  const M = { gold: GoldIcon, wood: WoodIcon, rum: RumIcon, prestige: PrestigeIcon }
  const C = M[name] ?? GoldIcon
  return <C {...p} />
}

/* ---------- buildings ---------- */
const BUILDING = {
  hq: <><path d="M4 21h16" /><path d="M5 21V10l7-5 7 5v11" /><path d="M9 21v-5h6v5" /><path d="M12 5V2.5" /></>,
  gold_mine: <><path d="M3 8c4-4 9-4 13 0" /><path d="M16 4l4 4" /><path d="M10 8l-6 12 3 1 5-11" /></>,
  sawmill: <><path d="M3 7h13l4 3-4 3H3z" /><path d="M16 7v6" /><path d="M3 17h11" /><path d="M5 17v3M9 17v3M13 17v3" /></>,
  distillery: <><path d="M9 4 Q7 11 9 15 L15 15 Q17 11 15 4 Z" /><path d="M15 8h3v4a3 3 0 0 1-3 3" /><path d="M9 15v3a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-3" /><path d="M9 7h6" /></>,
  warehouse: <><path d="M3 9l9-5 9 5v11H3z" /><path d="M3 13h18M9 13v7M15 13v7" /></>,
  shipyard: <><circle cx="12" cy="5" r="2" /><path d="M12 7v13" /><path d="M8 11H6m12 0h-2" /><path d="M5 13a7 7 0 0 0 14 0" /></>,
  // Tavern — a beer mug: tall body, side handle, foam head on top.
  tavern: (
    <>
      {/* mug body */}
      <path d="M7 10 h8 v9 a2 2 0 0 1 -2 2 H9 a2 2 0 0 1 -2 -2 Z" />
      {/* side handle */}
      <path d="M15 12 h2 a2 2 0 0 1 0 4 h-2" />
      {/* foam head — three arcing bumps */}
      <path d="M7 10 q1 -2.5 3 -1.5 q1 -2 3 -1 q1 1 2 2.5" />
    </>
  ),
  // Capitainerie — a lighthouse watching over the moorings.
  capitainerie: (
    <>
      {/* tower */}
      <path d="M9.5 21 L10.5 9 h3 L14.5 21" />
      {/* lamp room + light */}
      <path d="M10 9 V6 h4 v3" />
      <path d="M12 6 V4" />
      <path d="M7.5 5.5 9.5 7M16.5 5.5 14.5 7" />
      {/* quay */}
      <path d="M5 21h14" />
    </>
  ),
} as const
export function BuildingIcon({ type, ...p }: IconProps & { type: string }) {
  return <Svg {...p}>{BUILDING[type as keyof typeof BUILDING] ?? BUILDING.hq}</Svg>
}
// Generic "buildings" category glyph — a little skyline, distinct from the six
// specific building icons above.
export const BuildingsIcon = (p: IconProps) => (
  <Svg {...p}><path d="M4 21V9h7v12" /><path d="M11 21V4h9v17" /><path d="M3 21h18" /><path d="M6.5 13h2M6.5 17h2" /><path d="M14 8h3M14 12h3M14 16h3" /></Svg>
)

/* ---------- ship types ---------- */
const SHIP = {
  brick: <><path d="M14 4v11" /><path d="M14 5c2.6 .9 4.4 2.6 5.4 5C17 10.7 15.4 10.7 14 10.3" /><path d="M5 15h13l-2.4 4.5H7.4z" /><path d="M3 22c1.5 0 1.5-1 3-1s1.5 1 3 1 1.5-1 3-1 1.5 1 3 1 1.5-1 3-1" /></>,
  fregate: <><path d="M8 3v12M16 5v10" /><path d="M8 4c2.4 .8 4 2.2 4.8 4.4C10.6 9.1 9.2 9.1 8 8.7" /><path d="M16 6c2 .7 3.4 1.9 4.2 3.8C18.4 10.4 17.2 10.4 16 10" /><path d="M4 15h16l-2.2 4.5H6.2z" /><path d="M2 22c1.6 0 1.6-1 3.2-1s1.6 1 3.2 1 1.6-1 3.2-1 1.6 1 3.2 1 1.6-1 3.2-1" /></>,
  galion: <><path d="M6 5.5v9.5M11.5 3.5v11.5M17 6v9" /><path d="M6 6.5c2.1 .75 3.5 2 4.2 3.8C8.2 10.7 6.9 10.7 6 10.3" /><path d="M11.5 4.5c2.4 .9 4 2.2 4.8 4.2C14 9.2 12.7 9.2 11.5 8.8" /><path d="M17 7c1.8 .65 3 1.7 3.6 3.4C19 10 18 10 17 9.6" /><path d="M3 15h18l-2.4 4.5H5.4z" /><path d="M1.5 22c1.6 0 1.6-1 3.2-1s1.6 1 3.2 1 1.6-1 3.2-1 1.6 1 3.2 1 1.6-1 3.2-1" /></>,
  // Legendary — a grand three-master crowned with a guiding star.
  legendary: <><path d="M6 7v8.5M18 7v8.5M12 6.4V15.5" /><path d="M12 2.1Q12.4 3.9 14.2 4.3Q12.4 4.7 12 6.5Q11.6 4.7 9.8 4.3Q11.6 3.9 12 2.1Z" /><path d="M12 7.6c2.5 .8 4.2 2.4 5 4.5-2.4 .5-3.8 .5-5 .1" /><path d="M12 7.6c-2.5 .8-4.2 2.4-5 4.5 2.4 .5 3.8 .5 5 .1" /><path d="M4 15.5h16l-2.2 4.6H6.2z" /></>,
} as const
export function ShipIcon({ type, ...p }: IconProps & { type: string }) {
  return <Svg {...p}>{SHIP[type as keyof typeof SHIP] ?? SHIP.brick}</Svg>
}

/* ---------- combat stats ---------- */
const STAT = {
  manoeuvre: <><circle cx="12" cy="12" r="9" /><path d="M12 12l4-6-6 4z" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2" /></>,
  bordee: <><circle cx="7" cy="15" r="4" /><path d="M10 12l7-7" /><path d="M14 5h4v4" /></>,
  abordage: <><path d="M5 19L18 6M16 4h4v4" /><path d="M19 19L6 6M8 4H4v4" /></>,
  armure: <><path d="M12 3l7 2.5V11c0 5-3.2 8-7 10-3.8-2-7-5-7-10V5.5z" /></>,
  hp: <><path d="M12 20S4 14.5 4 8.8A4.2 4.2 0 0 1 12 6a4.2 4.2 0 0 1 8 2.8C20 14.5 12 20 12 20Z" /></>,
  cargo: <><rect x="4" y="8" width="16" height="12" rx="1" /><path d="M4 12h16M12 8v12" /></>,
  /** Équipage : deux marins épaule contre épaule (têtes + bustes). */
  crew: <><circle cx="9" cy="8" r="2.5" /><circle cx="16.5" cy="9" r="2" /><path d="M4 19v-1.5a5 5 0 0 1 10 0V19" /><path d="M15 19v-1a4 4 0 0 1 5-3.9" /></>,
} as const
export type StatName = keyof typeof STAT
export function StatIcon({ name, ...p }: IconProps & { name: StatName }) {
  return <Svg {...p}>{STAT[name]}</Svg>
}

/* ---------- misc ---------- */
export const TargetIcon = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" /></Svg>
)
export const PennantIcon = (p: IconProps) => (
  <Svg {...p}><path d="M9 28V4" /><path d="M9 6h14l-3.5 4.5L23 15H9" /><circle cx="9" cy="4" r="1.4" fill="currentColor" stroke="none" /></Svg>
)
export const ChevronRight = (p: IconProps) => (<Svg {...p}><path d="M9 6l6 6-6 6" /></Svg>)
export const ChevronDown = (p: IconProps) => (<Svg {...p}><path d="M6 9l6 6 6-6" /></Svg>)
export const CheckIcon = (p: IconProps) => (<Svg {...p}><path d="M5 12l4 4L19 7" /></Svg>)

/* ---------- classements / profil ---------- */
// Bourse — a drawstring purse, the commerce-gains stat on the captain profile.
export const PurseIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9.5 7.5h5l1.6 2.4C18 13 18.4 16 16.8 18 15.7 19.4 14 20 12 20s-3.7-.6-4.8-2C5.6 16 6 13 7.9 9.9Z" />
    <path d="M9.5 7.5 10.7 5h2.6l1.2 2.5" />
    <circle cx="12" cy="14" r="2.2" />
  </Svg>
)
// Trophée — an individual earned deed (un fait d'armes).
export const TrophyIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 4h10v3a5 5 0 0 1-10 0V4Z" />
    <path d="M7 6H4.5a2.5 2.5 0 0 0 2.5 2.5M17 6h2.5a2.5 2.5 0 0 1-2.5 2.5" />
    <path d="M12 12v3M9 19h6M10 19l.5-4h3l.5 4" />
  </Svg>
)
// Couronne — the Classements identity (rendered gold + glowing in the nav, and
// the Codex "Classements" section icon).
export const CrownIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 8.5l3.4 3L12 5.5l4.6 6L20 8.5l-1.4 9.5H5.4z" />
    <path d="M5.2 18h13.6" />
    <circle cx="4" cy="8.5" r="1.05" />
    <circle cx="20" cy="8.5" r="1.05" />
    <circle cx="12" cy="5.5" r="1.05" />
  </Svg>
)

/* ---------- navigation / overview ---------- */
export const IslandIcon = (p: IconProps) => (
  <Svg {...p}><path d="M3 21c2 0 2.5-1.2 4.5-1.2S10 21 12 21s2.5-1.2 4.5-1.2S20.5 21 22 21" /><path d="M11 20Q12 14 14 10" /><path d="M14 10q-3.4-1.8-6.4 .2M14 10q-1.2-3.4 .9-6.4M14 10q3.4-1.6 6.4 .5M14 10q2.2 .5 3.5 3" /><circle cx="14" cy="10.5" r="0.6" fill="currentColor" stroke="none" /></Svg>
)
export const MapIcon = (p: IconProps) => (
  <Svg {...p}><path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" /><path d="M9 4v14M15 6v14" /></Svg>
)
export const CombatIcon = (p: IconProps) => (
  <Svg {...p}><path d="M5 19L18 6M16 4h4v4" /><path d="M19 19L6 6M8 4H4v4" /><path d="M5 5l5 5M19 19l-5-5" /></Svg>
)
export const ForgeIcon = (p: IconProps) => (
  <Svg {...p}><path d="M4 8h13a5 5 0 0 1-5 5l1 3h2v2H7v-2h2l1-3a6 6 0 0 1-6-5z" /><path d="M17 8V5h3" /></Svg>
)
export const ModuleIcon = (p: IconProps) => (
  <Svg {...p}><rect x="6" y="6" width="12" height="12" rx="1.5" /><path d="M9 6V3M15 6V3M9 21v-3M15 21v-3M6 9H3M6 15H3M21 9h-3M21 15h-3" /></Svg>
)

/** The icon for a Forge branch — shared by the Forge page and the dashboard. */
export function ForgeBranchIcon({ branch, className = 'w-5 h-5 text-sand-300' }: { branch: string; className?: string }) {
  switch (branch) {
    case 'modules':
      return <ModuleIcon className={className} />
    case 'artillerie':
      return <StatIcon name="bordee" className={className} />
    case 'abordage':
      return <StatIcon name="abordage" className={className} />
    case 'charpente':
      return <StatIcon name="hp" className={className} />
    case 'logistique':
      return <CompassIcon className={className} />
    default:
      return <ForgeIcon className={className} />
  }
}

/* ---------- codex / lore ---------- */
// Codex — a gilded open tome (the rules & lore compendium).
export const BookIcon = (p: IconProps) => (
  <Svg {...p}><path d="M12 7c-2.2-1.6-5-2.5-8-2.5v13c3 0 5.8 .9 8 2.5 2.2-1.6 5-2.5 8-2.5v-13c-3 0-5.8 .9-8 2.5Z" /><path d="M12 7v13" /></Svg>
)
// Île mystérieuse — an open book crowned by a small spark: a "book of legends".
// The map + Codex marker for narrative expedition islands. Deliberately distinct
// from the Codex's plain open BookIcon so the map keeps one glyph per meaning
// (the bare "?" MysteryIcon now reads only as "undiscovered / under the fog").
export const TaleIcon = (p: IconProps) => (
  <Svg {...p}><path d="M12 9.6c-2.1-1.5-4.7-2.3-7.4-2.3v11.6c2.7 0 5.3 .8 7.4 2.3 2.1-1.5 4.7-2.3 7.4-2.3V7.3c-2.7 0-5.3 .8-7.4 2.3Z" /><path d="M12 9.6v11.6" /><path d="M12 1.9c.28 1.6 .66 1.98 2.26 2.26-1.6 .28-1.98 .66-2.26 2.26-.28-1.6-.66-1.98-2.26-2.26 1.6-.28 1.98-.66 2.26-2.26Z" /></Svg>
)
// Sablier — the async "le temps joue pour toi" glyph.
export const HourglassIcon = (p: IconProps) => (
  <Svg {...p}><path d="M6 3h12M6 21h12" /><path d="M8 3q0 6 4 9 4-3 4-9" /><path d="M8 21q0-6 4-9 4 3 4 9" /></Svg>
)
// Charte — a folded charter/contract, for the objectives ladder.
export const ScrollIcon = (p: IconProps) => (
  <Svg {...p}><path d="M8 3h7l3 3v12.5A2.5 2.5 0 0 1 15.5 21H8a2.5 2.5 0 0 1-2.5-2.5V5.5A2.5 2.5 0 0 1 8 3Z" /><path d="M15 3v3h3" /><path d="M9 11h6M9 14.5h4" /></Svg>
)
// Récit — a parchment scroll with a writing quill across it (the "Récits" nav
// glyph). A story that writes itself with your choices. Monochrome.
export const ScrollQuillIcon = (p: IconProps) => (
  <Svg {...p}><path d="M7 4h6l3 3v9a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z" /><path d="M13 4v3h3" /><path d="M8 16.5l7-7" /><path d="M14 8.5l2 2" /></Svg>
)
// Balance du marchand — identité du négoce (classement « Maîtres du négoce »).
export const BalanceIcon = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="4" r="1.3" /><path d="M12 5.3V19" /><path d="M6 8H18" /><path d="M6 8L4.3 11.6M6 8L7.7 11.6" /><path d="M3.8 11.6q2.2 2 4.4 0" /><path d="M18 8L16.3 11.6M18 8L19.7 11.6" /><path d="M15.8 11.6q2.2 2 4.4 0" /><path d="M9 19H15" /></Svg>
)
// Carte au trésor — a folded parchment with a dashed route and an X marking
// the spot (the active hunt). Distinct from MapIcon (the world-map nav glyph):
// here the dashed path + X are the whole point. Monochrome (tinted by Tailwind).
export const TreasureMapIcon = (p: IconProps) => (
  <Svg {...p}><path d="M4 5l4 1.5 8-2 4 1.5v13l-4-1.5-8 2-4-1.5z" /><path d="M8 6.5v13M16 4.5v13" /><path d="M6.5 10.5q2 0 2.5 2t2.5 2 2.5-2" strokeDasharray="1.6 1.6" /><path d="M16 12.5l3 3M19 12.5l-3 3" /></Svg>
)

/* ---------- world-map island types ---------- */
export const PortIcon = (p: IconProps) => (<Svg {...p}><path d="M12 3l7 2.5V11c0 5-3.2 8-7 10-3.8-2-7-5-7-10V5.5z" /><path d="M9 11l2 2 4-4" /></Svg>)
/** Lanterne de bord — chip « série de connexions » du header (allumée/éteinte via la couleur). */
export const LanternIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 3h4" /><path d="M12 3v2" />
    <path d="M9 5h6l1 2v8l-1 2H9l-1-2V7z" />
    <path d="M8 9h8M8 15h8" />
    {/* flamme */}
    <path d="M12 10.2c1 .9 1.5 1.6 1.5 2.4a1.5 1.5 0 0 1-3 0c0-.8.5-1.5 1.5-2.4z" fill="currentColor" stroke="none" />
    <path d="M10.5 19.5h3" />
  </Svg>
)
/** Bouée de sauvetage — jours d'escale (réserve anti jour manqué) de la carte au trésor. */
export const LifebuoyIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3.8" />
    <path d="M12 3.5v4.7M12 15.8v4.7M3.5 12h4.7M15.8 12h4.7" />
  </Svg>
)
export const SkullIcon = (p: IconProps) => (<Svg {...p}><circle cx="12" cy="10" r="7" /><circle cx="9" cy="9" r="1.4" fill="currentColor" stroke="none" /><circle cx="15" cy="9" r="1.4" fill="currentColor" stroke="none" /><path d="M12 12v2" /><path d="M8 17h8v2H8z" /></Svg>)
export const MysteryIcon = (p: IconProps) => (<Svg {...p}><circle cx="12" cy="12" r="9" opacity="0.5" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.4 2.3c-.8.4-.9.9-.9 1.7" /><circle cx="12" cy="16.5" r="0.6" fill="currentColor" stroke="none" /></Svg>)
export const StormIcon = (p: IconProps) => (<Svg {...p}><path d="M7 14a4 4 0 0 1 .6-7.9 5 5 0 0 1 9.5 1.4A3.5 3.5 0 0 1 17 14" /><path d="M13 11l-3 4h3l-1 4 4-5h-3z" fill="currentColor" /></Svg>)
// Récif : crête de rochers jagged que la ligne d'eau traverse (rochers à fleur d'eau),
// + un soupçon d'écume. Distinct du StormIcon (réservé aux tempêtes/aléas de traversée).
export const ReefIcon = (p: IconProps) => (<Svg {...p}><path d="M3.5 17 6 11 7.5 13.5 10.5 7 13 12.5 15.5 9.5 18 17" /><path d="M3 15q2.5-1 5 0t5 0t5 0" /><path d="M9.6 11.4 8.6 10.2M11.4 11.4 12.4 10.2" /></Svg>)
export const MineIcon = (p: IconProps) => (<Svg {...p}><path d="M3 8c4-4 9-4 13 0" /><path d="M16 4l4 4" /><path d="M10 8l-6 12 3 1 5-11" /></Svg>)
export const ScopeIcon = (p: IconProps) => (<Svg {...p}><path d="M4 14l9-9 2 2-7 9-4 1z" /><circle cx="17.5" cy="6.5" r="2.5" /><path d="M9 13l2 2" /></Svg>)
export const CompassIcon = (p: IconProps) => (<Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M15.5 8.5l-2 5-5 2 2-5z" fill="currentColor" stroke="none" /></Svg>)
export const CloseIcon = (p: IconProps) => (<Svg {...p}><path d="M6 6l12 12M18 6L6 18" /></Svg>)
export const MenuIcon = (p: IconProps) => (<Svg {...p}><path d="M4 7h16M4 12h16M4 17h16" /></Svg>)
// Bulle de chat — the feedback ("Un bug ? Une idée ? Une question ?") glyph.
// A rounded speech bubble with a tail + two message lines; reads clearly at FAB
// size, unlike a detailed standalone icon. Monochrome (inherits currentColor).
export const ChatBubbleIcon = (p: IconProps) => (
  <Svg {...p}><path d="M5 4.5h14a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5H10l-4 3.5V15.5H5A1.5 1.5 0 0 1 3.5 14V6A1.5 1.5 0 0 1 5 4.5Z" /><path d="M7.5 9h9M7.5 12h5.5" /></Svg>
)
// The captain's scarlet macaw — a deliberately COLORED icon (breaks the
// monochrome line set on purpose) for the notification surface. A red parrot
// with a hooked beak says "pirate" at a glance. Fixed fills, sized via className.
export const ParrotIcon = ({ className, ...p }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...p}>
    {/* perch */}
    <rect x="6" y="20.8" width="10.5" height="1.8" rx=".9" fill="#8a5a2b" />
    {/* long tail */}
    <path d="M13 12c2.4 2.8 4.1 6.3 3.4 9.2l-2.4-.8c-1.2-2.7-2-5.5-1.8-8z" fill="#c62f25" />
    {/* body */}
    <path d="M11 7.4c3.5 0 5.4 3 5.1 6.5s-2.5 6-5.1 6-4.6-2.4-4.6-5.9 1.6-6.6 4.6-6.6z" fill="#ec4a35" />
    {/* blue + gold wing */}
    <path d="M12.4 9.6c2.4.8 2.9 4 1.4 6.7-1-2-1.7-4.3-1.4-6.7z" fill="#2f6fd0" />
    <path d="M12.5 10.3c1.4.7 1.6 2.9.7 4.7-.6-1.7-.9-3.2-.7-4.7z" fill="#f3c41a" />
    {/* head */}
    <circle cx="9.6" cy="6.6" r="4.3" fill="#ec4a35" />
    {/* pale face patch */}
    <path d="M6.3 4.7c2-1 3.9-.4 4.7 1.2-.9 1.8-3.2 2.3-4.8 1.1-.6-.7-.5-1.7.1-2.3z" fill="#fcf4e1" />
    {/* eye */}
    <circle cx="9" cy="6" r="1.1" fill="#171717" />
    {/* big hooked beak */}
    <path d="M6 5.1C2.4 4.7.8 6.8 1.8 9.3 3.4 8.8 5 7.9 6.4 6.6z" fill="#262626" />
    <path d="M2.2 8.3c.7 1 1.9 1.2 2.9.6-.8-1-1.9-1.2-2.9-.6z" fill="#5f5f5f" />
    {/* feet */}
    <path d="M10 19.6v1.4M11.9 19.6v1.4" stroke="#caa15a" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
)
// Bouteille à la mer — a deliberately COLORED standalone icon (like ParrotIcon)
// for a newly-offered quest: a tilted glass bottle bobbing on a wavelet, a cork
// in the neck and a rolled parchment hinted inside. Reads as "une quête vient
// d'échouer sur le rivage". Fixed fills, sized via className.
export const BottleIcon = ({ className, ...p }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...p}>
    {/* glass body (tilted), low-opacity glass-green */}
    <path d="M6.3 18.4 13.7 4.6a1.6 1.6 0 0 1 2.8 1.5L9.1 19.9a1.6 1.6 0 0 1-2.8-1.5z" fill="#3f9e87" opacity="0.45" stroke="#2f7d6c" strokeWidth="0.9" />
    {/* rolled parchment hinted inside */}
    <path d="M9.4 16.7 13 9.9" stroke="#e7d3a1" strokeWidth="1.4" strokeLinecap="round" />
    {/* cork at the neck */}
    <path d="M14.5 3.9 17 5.2a1.1 1.1 0 0 1-1 2L13.5 5.9z" fill="#9c6a36" stroke="#774e22" strokeWidth="0.7" />
    {/* ocean wavelet under the bottle */}
    <path d="M3 20.5q2-1.4 4 0t4 0 4 0 4 0 2 0" fill="none" stroke="#2f6fd0" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
)

// Grimoire — the "Récits" emblem. A closed, ornate leather tome (gilded border
// + a gemmed clasp medallion), a deliberately COLORED standalone icon like
// ParrotIcon/BottleIcon — distinct from the Codex's open BookIcon. Fixed fills,
// sized via className.
export const GrimoireIcon = ({ className, ...p }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...p}>
    {/* cream page block, peeking on the right + bottom */}
    <rect x="6" y="4.6" width="12.6" height="15" rx="1.3" fill="#efe2bd" />
    {/* leather front cover */}
    <rect x="4" y="3.6" width="13" height="16" rx="1.7" fill="#7a4a22" stroke="#3f230f" strokeWidth="0.8" />
    {/* darker spine band on the left */}
    <rect x="4" y="3.6" width="2.7" height="16" rx="1.3" fill="#542f15" />
    {/* gilded inner border on the cover */}
    <rect x="7.7" y="5.5" width="7.8" height="12.2" rx="0.8" fill="none" stroke="#d8b25a" strokeWidth="0.7" />
    {/* clasp medallion + gem */}
    <circle cx="11.6" cy="11.6" r="2.4" fill="#caa15a" stroke="#7a5320" strokeWidth="0.5" />
    <circle cx="11.6" cy="11.6" r="1.25" fill="#2f6fd0" />
    <circle cx="11.2" cy="11.2" r="0.45" fill="#bfe0ff" />
  </svg>
)

/* ---------- layout / view switches ---------- */
export const GridIcon = (p: IconProps) => (
  <Svg {...p}><rect x="4" y="4" width="6.5" height="6.5" rx="1.2" /><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.2" /><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.2" /><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.2" /></Svg>
)
export const ListIcon = (p: IconProps) => (
  <Svg {...p}><path d="M8 6h12M8 12h12M8 18h12" /><circle cx="4" cy="6" r="1" fill="currentColor" stroke="none" /><circle cx="4" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="4" cy="18" r="1" fill="currentColor" stroke="none" /></Svg>
)

/** Plume + pièce — la table des négociations (diplomatie & corruption). */
export const DiplomacyIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M19 4c-4 0-8 3-9.5 7L8 15l3-1c4-1.5 8-6 8-10z" />
    <path d="M8 15l-2 5" />
    <circle cx="17" cy="18" r="3" />
    <path d="M15.9 18h2.2" />
  </Svg>
)

/* ---------- defense structures ---------- */
// Milice — deux sabres de pirate croisés (lames courbes, gardes + poignées).
export const SabresIcon = (p: IconProps) => (
  <Svg {...p}><path d="M4 20 C8 14 12 10 19 5" /><path d="M19 5 19 8 M19 5 16 5" /><path d="M3.2 18.6 L5.4 20.8" /><path d="M4 20 L2.6 21.4" /><path d="M20 20 C16 14 12 10 5 5" /><path d="M5 5 5 8 M5 5 8 5" /><path d="M20.8 18.6 L18.6 20.8" /><path d="M20 20 L21.4 21.4" /></Svg>
)
// Tour de guet — vigie en bois (cabine à toit pointu sur pilotis évasés).
export const WatchtowerIcon = (p: IconProps) => (
  <Svg {...p}><path d="M6.5 9 L12 4.5 L17.5 9" /><path d="M8 9 H16 V13 H8 Z" /><path d="M11 10.4 H13 V13" /><path d="M9 13 L7.5 21 M15 13 L16.5 21" /><path d="M8.3 17 H15.7" /><path d="M6 21 H18" /></Svg>
)
// Fort — donjon crénelé (muraille + créneaux + porte).
export const FortIcon = (p: IconProps) => (
  <Svg {...p}><path d="M4 21V6h2.5v2.5h2.5V6h2.5v2.5h2.5V6h2.5v2.5h2.5V6h1v15" /><path d="M3 21h18" /><path d="M9.5 21v-5h5v5" /></Svg>
)
// Batterie côtière — canon sur affût à roues.
export const BatteryIcon = (p: IconProps) => (
  <Svg {...p}><rect x="5" y="8" width="13" height="3.5" rx="1.75" /><path d="M18 9.75h2" /><path d="M6.5 11.5 8 14.5M16 11.5l-1.2 3.8" /><circle cx="8.5" cy="17" r="2.5" /><circle cx="14.7" cy="17.5" r="1.8" /></Svg>
)
// Dispatcher défense → l'icône de la structure (réutilisé dashboard + DefensePanel).
export function DefenseIcon({ type, ...p }: IconProps & { type: string }) {
  switch (type) {
    case 'battery':
      return <BatteryIcon {...p} />
    case 'militia':
      return <SabresIcon {...p} />
    case 'watchtower':
      return <WatchtowerIcon {...p} />
    default:
      return <FortIcon {...p} />
  }
}

// Cabine du capitaine — buste silhouette dans un contour de blason (identité
// hybride portrait/blason). Réutilisé Codex + cloche de notifications.
export const CabineIcon = (p: IconProps) => (
  <Svg {...p}><path d="M12 3l7 2.5V11c0 5-3.2 8-7 10-3.8-2-7-5-7-10V5.5z" /><circle cx="12" cy="9.6" r="2.4" /><path d="M8.3 16c.8-2 2.1-3 3.7-3s2.9 1 3.7 3" /></Svg>
)
